import { Link } from 'react-router-dom';
import { Check, ShoppingBag, Calendar, CreditCard, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Afterpay = () => {
  const steps = [
    {
      icon: ShoppingBag,
      title: "Adicione ao Carrinho",
      description: "Escolha seus produtos favoritos e adicione ao carrinho"
    },
    {
      icon: CreditCard,
      title: "Selecione Afterpay",
      description: "No checkout, escolha Afterpay como método de pagamento"
    },
    {
      icon: Calendar,
      title: "Parcele em 4x",
      description: "Pague em 4 parcelas iguais a cada 2 semanas, sem juros"
    },
    {
      icon: Check,
      title: "Receba seu Pedido",
      description: "Seu pedido é enviado imediatamente após a aprovação"
    }
  ];

  const benefits = [
    "Sem juros - você paga apenas o valor do produto",
    "Aprovação instantânea na maioria dos casos",
    "Primeira parcela no ato da compra",
    "Parcelas automáticas a cada 2 semanas",
    "Sem consulta ao SPC/Serasa",
    "Proteção ao comprador incluída"
  ];

  return (
    <div className="min-h-screen" data-testid="afterpay-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-[#b2fce4]/20 to-background">
        <div className="container-luxury text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#b2fce4]/20 rounded-full mb-6">
            <span className="text-[#b2fce4] font-semibold">AFTERPAY</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Compre Agora, Pague em
            <span className="text-[#b2fce4]"> 4x Sem Juros</span>
          </h1>
          
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Com o Afterpay, você divide suas compras em 4 parcelas iguais, 
            sem pagar nada a mais por isso.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/shop">
              <Button className="btn-primary h-14 px-8 text-lg">
                <ShoppingBag className="w-5 h-5 mr-2" />
                Começar a Comprar
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20">
        <div className="container-luxury">
          <h2 className="text-3xl font-bold text-center mb-12">Como Funciona</h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#b2fce4]/20 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                  <step.icon className="w-8 h-8 text-[#b2fce4]" />
                  <span className="absolute -top-2 -right-2 w-6 h-6 bg-[#b2fce4] text-black rounded-full text-sm font-bold flex items-center justify-center">
                    {index + 1}
                  </span>
                </div>
                <h3 className="font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Example */}
      <section className="py-20 bg-card/50">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Exemplo de Pagamento</h2>
            
            <div className="bg-gradient-to-br from-[#b2fce4]/10 to-background border border-[#b2fce4]/30 p-8 rounded-lg">
              <div className="text-center mb-8">
                <p className="text-muted-foreground mb-2">Valor Total da Compra</p>
                <p className="text-5xl font-bold text-[#b2fce4]">$100.00</p>
              </div>
              
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="p-4 bg-card rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Hoje</p>
                  <p className="text-xl font-bold">$25</p>
                </div>
                <div className="p-4 bg-card rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">2 semanas</p>
                  <p className="text-xl font-bold">$25</p>
                </div>
                <div className="p-4 bg-card rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">4 semanas</p>
                  <p className="text-xl font-bold">$25</p>
                </div>
                <div className="p-4 bg-card rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">6 semanas</p>
                  <p className="text-xl font-bold">$25</p>
                </div>
              </div>
              
              <p className="text-center text-sm text-muted-foreground mt-6">
                Total pago: $100.00 • Sem juros • Sem taxas ocultas
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="container-luxury">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Benefícios do Afterpay</h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#b2fce4]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-[#b2fce4]" />
                    </div>
                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-card border border-white/10 p-8 rounded-lg">
              <Shield className="w-12 h-12 text-[#b2fce4] mb-4" />
              <h3 className="text-xl font-bold mb-3">Compra Segura</h3>
              <p className="text-muted-foreground mb-6">
                O Afterpay é uma das maiores plataformas de "compre agora, pague depois" do mundo, 
                com milhões de usuários satisfeitos.
              </p>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>🔒 Criptografia SSL</span>
                <span>✓ Proteção ao Comprador</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Limits */}
      <section className="py-20 bg-card/50">
        <div className="container-luxury text-center">
          <h2 className="text-3xl font-bold mb-6">Limites de Compra</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            O Afterpay aceita compras entre $1 e $2,000 USD. 
            O limite pode variar de acordo com seu histórico.
          </p>
          
          <div className="inline-flex items-center gap-8 p-6 bg-card border border-white/10 rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Mínimo</p>
              <p className="text-2xl font-bold">$1</p>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <p className="text-sm text-muted-foreground">Máximo</p>
              <p className="text-2xl font-bold">$2,000</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container-luxury text-center">
          <h2 className="text-3xl font-bold mb-6">Pronta para Comprar?</h2>
          <p className="text-muted-foreground mb-8">
            Explore nossa coleção e parcele suas compras com Afterpay
          </p>
          <Link to="/shop">
            <Button className="btn-primary h-14 px-12 text-lg">
              Ver Coleção
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Afterpay;
